import React, { useEffect, useMemo, useRef, useState } from "react";
import { Popconfirm, message } from "antd";
import { deleteItem } from "../../api/OrdersApi";
import { duplicateItem } from "../../api/OrderItemUnits";
import { renderAlertMessage } from "../../helpers/common";
import { useItemsMeasureUnitsContext } from "./ItemsMeasureUnits";
import UnitsTable, {
  computeHandlingUnitsPreview,
  computeLiveMismatches,
  computeNewItemUnitsPreview,
} from "./UnitsTable";
import { openSelectOnEnter } from "./selectKeyboard";
import { toDecimalInput, toIntegerInput } from "./itemDraftHelpers";
import { itemDimWeightLb } from "./itemMeasurePreview";

const LB_TO_KG = 0.453592;

// "Pieces" / "Handling Units" are NOT selectable types (user decision) — the
// dropdowns lead with a disabled "Select ..." placeholder instead, and a new
// item starts blank until the user picks a real type.
const PIECE_TYPES = [
  "Piece",
  "Cartons",
  "Boxes",
  "Pallets",
  "Skids",
  "Cases",
  "Crates",
  "Drums",
  "ULDs",
  "Air Trays",
];

const HANDLING_UNIT_TYPES = [
  "Handling Unit",
  "Cartons",
  "Boxes",
  "Pallets",
  "Skids",
  "Cases",
  "Crates",
  "Drums",
  "ULDs",
  "Air Trays",
];

// Same rollup convention as the mockup: all units sharing one status show
// that status; otherwise the highest-priority status present is featured as
// "Partially <status>". No units at all → pill is hidden entirely.
const STATUS_PRIORITY = [
  "Exception",
  "Delivered",
  "On Hand",
  "In Transit",
  "Loaded",
  "Received",
  "Pending",
];

function fmt(value, decimals = 2) {
  const num = parseFloat(value);
  if (value === null || value === undefined || Number.isNaN(num)) {
    return (0).toFixed(decimals);
  }
  return num.toFixed(decimals);
}

function lbToKg(lb) {
  return (parseFloat(lb) || 0) * LB_TO_KG;
}

// OrderItemUnit#convert_to_lb treats only 'kg'/'kgs' as kilograms; everything
// else (including blank) is already pounds. Mirrored here for display only.
function toLb(value, uom) {
  const num = parseFloat(value) || 0;
  return ["kg", "kgs"].includes(String(uom || "").toLowerCase())
    ? num / LB_TO_KG
    : num;
}

function toInches(value, uom) {
  const num = parseFloat(value || 0);
  if (Number.isNaN(num)) return 0;
  return uom === "cm" ? num / 2.54 : num;
}

function rollupStatus(units) {
  if (!units || units.length === 0) return null;
  const statuses = units.map((u) => u.status);
  const allSame = statuses.every((s) => s === statuses[0]);
  if (allSame) return { label: statuses[0], partial: false };
  let featured = "Pending";
  for (const s of STATUS_PRIORITY) {
    if (statuses.includes(s)) {
      featured = s;
      break;
    }
  }
  return { label: `Partially ${featured}`, partial: true };
}

// M4 (mockup-exact, docs/order-item-units-frontend-plan-2): the mockup's
// .split-error blocking panel uses fixed copy per mismatch code, not the
// value-bearing message computeLiveMismatches / OrderItemUnitSyncService
// .mismatches produce (e.g. "Unit weights add up to 33.34 of 40.0 lb") —
// that richer message stays on the small live mismatch-chips above; only the
// blocking PANEL text below switches to this exact mockup copy. Falls back
// to the raw message for any future/unmapped mismatch code rather than
// rendering nothing.
const MISMATCH_PANEL_TEXT = {
  piece_split_mismatch: "Piece split doesn't match the total pieces.",
  weight_mismatch: "Scannable unit weights don't add up to the item's total weight.",
};
function mismatchPanelText(m) {
  return MISMATCH_PANEL_TEXT[m.code] || m.message;
}

function statusPillClass(status, partial) {
  if (partial) return "partial";
  if (status === "Exception") return "exception";
  if (!status || status === "Pending") return "";
  return "progress";
}

// Item-level "(Actual)" weight/dim total, aggregated from already-persisted
// per-unit reweigh_* values (display only — never written back to the
// server, same convention as ItemsMeasureUnits.computeVolumeFt3).
function reweighWeightAvailable(units) {
  return (
    units.length > 0 &&
    units.every((u) => u.reweigh_weight !== null && u.reweigh_weight !== undefined && u.reweigh_weight !== "")
  );
}

function reweighWeightTotal(units) {
  return units.reduce((sum, u) => sum + toLb(u.reweigh_weight, u.reweigh_weight_uom), 0);
}

function reweighDimAvailable(units) {
  return (
    units.length > 0 &&
    units.every(
      (u) =>
        u.reweigh_length !== null &&
        u.reweigh_length !== undefined &&
        u.reweigh_length !== "" &&
        u.reweigh_width !== null &&
        u.reweigh_width !== undefined &&
        u.reweigh_width !== "" &&
        u.reweigh_height !== null &&
        u.reweigh_height !== undefined &&
        u.reweigh_height !== ""
    )
  );
}

function reweighDimWeightTotal(units, dimFactor) {
  const factor = parseFloat(dimFactor);
  if (!(factor > 0)) return null;
  return units.reduce((sum, u) => {
    const l = toInches(u.reweigh_length, u.reweigh_dim_uom);
    const w = toInches(u.reweigh_width, u.reweigh_dim_uom);
    const h = toInches(u.reweigh_height, u.reweigh_dim_uom);
    return sum + (l * w * h) / factor;
  }, 0);
}

const CopyIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18" />
    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

const ChevronIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="18 15 12 9 6 15" />
  </svg>
);

// Collapsible item card: header (name/chips/meta/rollup pill/actions), field
// row (description/model/serial/location/pieces/HUs/weight/dims), mismatch
// chips + blocking error panel, and the UnitsTable (booked/re-weigh
// scannable-units editing; selection + mode-toggle state for it lives here
// so BulkApplyRow can reuse the same selection shape).
//
// Part A: item-field edits are draft-until-Save now. `draft`/`touched` and
// `collapsed` are CONTROLLED props owned by the container
// (ItemsMeasureUnits) — this component never persists a field edit itself
// and never owns collapse state, so neither survives-a-reload nor
// draft-persistence has to be re-solved here. The only immediate (non-draft)
// writes left in this component are Duplicate/Delete of an already-PERSISTED
// item (unchanged from before) — unit rows/bulk/swap/status stay immediate
// too, untouched, inside UnitsTable.
//
// `isNew` cards (from "Add N Items", not yet saved) have no server item, no
// units, and no collapse — they render expanded until Save creates them for
// real, per the plan's "keep simple" note.
// Stable empty default (Editable-units fix): an item with no pending unit
// edits yet must get a referentially-stable {existing:{}, added:{}} rather
// than a fresh literal every render, same convention as UnitsTable's
// EMPTY_UNITS_PREVIEW / EMPTY_ITEM_UNIT_EDITS.
const EMPTY_ITEM_UNIT_EDITS = { existing: {}, added: {} };

const ItemCard = ({
  item,
  units = [],
  // Container's resolved dim factor (an item's dim_factor, else the 139
  // fallback) — used to compute a live DIM WT from the draft dims, same source
  // the top-panel Dim WT uses, so the header reflects edited dims.
  dimFactor,
  // The account's configured DIM-factor options for the per-item Dim dropdown.
  configuredDimFactors = [],
  // The account's default DIM factor — the dropdown's default value when the
  // item has none (legacy rule: value = item.dim_factor || accountDim.dim_factor).
  accountDimFactor = null,
  mismatches = [],
  readOnly = false,
  draft,
  touched = {},
  onFieldChange,
  onSelectChange,
  collapsed = false,
  onToggleCollapse = () => {},
  isNew = false,
  onRemoveNew = () => {},
  // Clone a not-yet-saved item card (copy icon on the create flow).
  onDuplicateNew = () => {},
  // Cancel this (persisted) card's edits — revert its draft to the saved
  // values and collapse. New cards use onRemoveNew instead.
  onCancelEdit = () => {},
  // Editable-units fix (docs/order-item-units-frontend-plan-2): this item's
  // slice of the container-owned pendingUnitEdits map, and its setter —
  // threaded straight down to UnitsTable/UnitRow.
  pendingUnitEdits = EMPTY_ITEM_UNIT_EDITS,
  onUnitPendingEdit = () => {},
  // Audit fix I3: true for exactly as long as this item's id is in the
  // container's mismatchSuppressedIds (a just-Saved item whose post-Save
  // reload hasn't landed yet). Previously the container only suppressed the
  // `mismatches` PROP (the read-only-mode source), but editable-mode's
  // effectiveMismatches recomputes LIVE from the same (still stale, pre-
  // reload) draft/units/preview every render — so a save that cleared a
  // mismatch could still flash the chip/blocked-Save state for one render
  // between "Save succeeded" and "reload landed". Folding suppression into
  // effectiveMismatches itself (below), in BOTH modes, closes that gap.
  suppressMismatches = false,
  // Group Items (Figma "Group Order" frames): when true this card renders as
  // a single collapsed row with a left checkbox instead of its normal
  // header+body — every edit affordance (duplicate/delete/chevron/field row/
  // units table) is hidden, matching the "State=Grouping"/"State=Selected"
  // item frames (3689:29522 / 3689:29534). `selected` drives the indigo
  // tint+border (.item-card-selected); `onToggleSelect` is a zero-arg
  // callback the container already bound to this item's id.
  groupMode = false,
  selected = false,
  onToggleSelect = () => {},
  // Org config (Items & Measure Fields): keys the org chose to hide. `model`
  // / `serial_number` drop those fields; `handling_unit_type` and `piece_type`
  // independently drop their selectors (the numeric Pieces / HUs stay, since
  // the scannable-unit count depends on HUs).
  hiddenFields = {},
  // Per-item live rollup from the container (computeLiveUnitTotals for this
  // item) — { bookedWeightLb, bookedDimLb, reweighWeightLb, reweighDimLb }.
  // Lets the header meta reflect unit edits as they're typed instead of only
  // after save. Null-valued fields mean "no live value; use the persisted one".
  liveMeta = null,
  // PO point 7: this card has unsaved edits (a dirty item draft or unflushed
  // pending unit edits, or it's a never-saved new card). Drives the "editing"
  // border that stays until the item is saved again.
  dirty = false,
  // Org config: 'quantity' (DIM weight × pieces) or 'handling_units' (× HUs).
  // Drives the DIM WT figure.
  dimWeightBasis = "handling_units",
  // Org billing rule: when true, item L/W/H are mandatory — a blank dim blocks
  // Save/collapse with an error, same as a piece/weight mismatch.
  requiredDims = false,
  // Org setting require_items_on_order_creation: when true, item weight is
  // mandatory — a blank/zero weight blocks Done/Save with an error, exactly
  // like requiredDims does for L/W/H.
  requireItems = false,
}) => {
  const { reload, refreshDetails, deferMutations, stageDeleteItem, stageDuplicateItem } =
    useItemsMeasureUnitsContext();

  const [closeAttempted, setCloseAttempted] = useState(false);
  // Booked/Re-Weighed toggle + row-selection state for this item's
  // UnitsTable. Kept here (not inside UnitsTable) so BulkApplyRow can reuse
  // the exact same {checkedIds, onToggleCheck, onToggleAll} shape.
  const [scanMode, setScanMode] = useState("booked");
  const [checkedIds, setCheckedIds] = useState(() => new Set());

  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Frontend Task 11 / Wave A: the live preview object, computed HERE (the
  // owner of checkedIds) via the single shared derivation in UnitsTable and
  // passed down, so the selection layer and the rendering layer can never
  // disagree about which units are "removed on Save". Memoized so
  // preview.removedIds is referentially stable across unrelated re-renders —
  // the prune effect below keys on it.
  //
  // Wave A extends this from a pure HUs-count preview into a full
  // display-value preview (per-unit piece split/booked weight/booked dims/
  // storage location, plus the draft-aware split lock) — `draft`/`item` are
  // now passed through whole so computeHandlingUnitsPreview can read every
  // field its formulas need (see UnitsTable.jsx for the full derivation and
  // its server file:line mappings).
  // Units-during-create fix (the reported bug: "after creation of item
  // scannable units are popping, but while creating the item it should
  // work add scannable units"): a pending "Add N Items" card has no
  // persisted item to diff a draft against, so it gets its own
  // unconditionally-active preview (computeNewItemUnitsPreview) instead of
  // computeHandlingUnitsPreview's dirty-gated one — every seed row IS the
  // preview, there's no "before" state to compare against.
  const preview = useMemo(
    () =>
      isNew
        ? computeNewItemUnitsPreview({ draft, readOnly })
        : computeHandlingUnitsPreview({ units, draft, item, readOnly, pendingEdits: pendingUnitEdits.existing }),
    [isNew, units, draft, item, readOnly, pendingUnitEdits]
  );

  // Draft-until-Save for scannable units (user decision): whenever the box is
  // EDITABLE, every unit-cell edit accumulates in pendingUnitEdits and is
  // flushed by the box's Save (batched bulk_upsert), exactly like the item
  // fields — instead of PATCHing per cell on blur. This is what keeps the
  // "unsaved" border on the card until Save (a unit edit marks the item dirty
  // via pendingUnitEdits) and collapses the per-cell API calls into one. It
  // also covers the original previewing/added-rows cases (preview.previewActive
  // / addCount) since those only ever occur while !readOnly. Never in view mode
  // (readOnly), where the inputs are disabled anyway.
  const deferSaves = !readOnly;

  // B2 (mockup-parity) + live-warning requirement: whenever the box is
  // EDITABLE, the chips/panel/Done-blocking recompute the mismatches live on
  // every keystroke — from the draft, the derivation preview, and any pending
  // unit-cell edits (computeLiveMismatches, a line-for-line mirror of
  // OrderItemUnitSyncService.mismatches). This covers unit-split edits with a
  // clean item draft too (previously only draft-dirty items recomputed, so a
  // split typo surfaced only after Done/Save). With nothing dirty the live
  // computation resolves to exactly the persisted values, so it agrees with
  // the server's own mismatches; in read-only contexts we show the server
  // prop directly (a persisted mismatch is real — manual splits are never
  // auto-overridden).
  const effectiveMismatches = useMemo(
    () =>
      suppressMismatches
        ? []
        : readOnly
        ? mismatches || []
        : computeLiveMismatches({ units, draft, preview, pendingEdits: pendingUnitEdits }),
    [suppressMismatches, readOnly, preview, units, draft, mismatches, pendingUnitEdits]
  );

  // Required DIMs (org billing rule) is NOT a live warning — a fresh item has
  // blank dims, and flagging it on every keystroke is noise. It surfaces only
  // on a finalize ATTEMPT (Done / Save), via `blockers` below.
  const dimsRequiredMissing =
    !readOnly &&
    requiredDims &&
    [draft.item_length, draft.item_width, draft.item_height].some((v) => v === "" || v === null || v === undefined);
  // Required weight (org setting require_items_on_order_creation): like
  // dimsRequiredMissing, this is NOT a live warning — it only surfaces on a
  // finalize ATTEMPT (Done / Save), via `blockers`. weight_total_exact is the
  // draft's source-of-truth weight (any uom); blank/zero means "no weight".
  const weightRequiredMissing =
    !readOnly && requireItems && !((parseFloat(draft.weight_total_exact) || 0) > 0);
  // Blockers = the live piece/weight mismatches PLUS the dims-required and
  // weight-required rules. Drives the Done/collapse block, the collapsed-card
  // badge, and the on-attempt error panel — but NOT the live mismatch chips
  // (those stay effectiveMismatches so these messages never show before an
  // attempt).
  const blockers = [
    ...effectiveMismatches,
    ...(dimsRequiredMissing ? [{ code: "dims_required", message: "Dimensions (L × W × H) are required" }] : []),
    ...(weightRequiredMissing ? [{ code: "weight_required", message: "Weight is required" }] : []),
  ];

  // Drop any selected ids that no longer exist on this item (e.g. HUs count
  // shrank and the server removed units) so a stale checkbox can't linger
  // selected forever.
  useEffect(() => {
    setCheckedIds((prev) => {
      const validIds = new Set(units.map((u) => u.id));
      let changed = false;
      const next = new Set();
      prev.forEach((id) => {
        if (validIds.has(id)) {
          next.add(id);
        } else {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [units]);

  // Same prune, one step earlier in the lifecycle (Frontend Task 11): a unit
  // checked BEFORE the user shrank the HUs draft must fall out of the
  // selection the moment its row flips to the preview-removed state — its
  // checkbox renders forced-unchecked/disabled, so a surviving id would give
  // BulkApplyRow a live PATCH path to a row displayed as locked. UnitsTable
  // additionally filters removedIds out of the ids it hands BulkApplyRow
  // (belt-and-braces for the render(s) before this effect commits).
  useEffect(() => {
    if (preview.removedIds.size === 0) return;
    setCheckedIds((prev) => {
      let changed = false;
      const next = new Set();
      prev.forEach((id) => {
        if (preview.removedIds.has(id)) {
          changed = true;
        } else {
          next.add(id);
        }
      });
      return changed ? next : prev;
    });
  }, [preview.removedIds]);

  // NOTE: selection is intentionally NOT cleared when the box locks — view
  // mode now supports selecting units (for the read-only Download-labels bulk
  // action, user request). No stale write path results: in read-only
  // BulkApplyRow renders without edit fields/Apply and handleApply itself
  // early-returns on readOnly.

  const handleToggleCheck = (unitId) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(unitId)) {
        next.delete(unitId);
      } else {
        next.add(unitId);
      }
      return next;
    });
  };

  // Select-all only ever selects SELECTABLE units — preview-removed rows are
  // excluded so the header checkbox can never pull a "removed on Save" unit
  // into the selection (UnitsTable computes its checked/indeterminate header
  // state over the same filtered set).
  const handleToggleAll = (nextChecked) => {
    setCheckedIds(
      nextChecked
        ? new Set(units.filter((u) => !preview.removedIds.has(u.id)).map((u) => u.id))
        : new Set()
    );
  };

  // Text inputs: update the draft only. Persistence now happens exclusively
  // via the container's Save button (handleSaveAll in ItemsMeasureUnits).
  const handleFieldChange = (field, value) => {
    if (onFieldChange) onFieldChange(field, value);
  };

  const handleSelectChange = (field, value) => {
    if (onSelectChange) onSelectChange(field, value);
  };

  // Duplicate/Delete of a PERSISTED item stay immediate (unaffected by Part
  // A's draft model).
  //
  // B1 (mockup-parity fix): duplicate used to resubmit only item-level
  // fields through the legacy create endpoint, whose after_save unit-sync
  // hook then minted a brand-new set of DEFAULT units for the duplicate —
  // silently discarding the source item's manual piece splits, booked/
  // reweigh measurements, and storage locations. Now a single atomic
  // server-side call (API::V2::OrderItemUnitsController#duplicate_item)
  // duplicates the item AND overlays the source units' data onto the new
  // item's units, so nothing per-unit is lost. It operates on the
  // PERSISTED item (not this card's on-screen `draft`) — any not-yet-saved
  // edits are no longer carried into the duplicate; Save first if those
  // should be included.
  const handleDuplicate = (e) => {
    e.stopPropagation();
    if (readOnly) return;
    // A not-yet-saved card has no server id to duplicate — clone its draft
    // locally into another new-item card instead (container handler).
    if (isNew) {
      onDuplicateNew();
      return;
    }
    // Inline-edit: defer to Save — stage an editable local clone, no API now.
    if (deferMutations && typeof stageDuplicateItem === "function") {
      stageDuplicateItem(item);
      return;
    }
    duplicateItem(item.id).then((result) => {
      if (!isMounted.current) return;
      if (result.success) {
        reload();
        // Duplicate persists immediately (no order Save) and changes the item
        // count / weight, so refresh the order-details header too.
        if (refreshDetails) refreshDetails();
      } else {
        renderAlertMessage(result.errors);
      }
    });
  };

  const handleDelete = (e) => {
    if (e) e.stopPropagation();
    if (readOnly) return;
    if (isNew) {
      // A never-saved draft card: nothing exists server-side yet, just drop
      // it locally (Part A2).
      onRemoveNew();
      return;
    }
    // Inline-edit: defer to Save — stage for deletion, no API now.
    if (deferMutations && typeof stageDeleteItem === "function") {
      stageDeleteItem(item.id);
      return;
    }
    deleteItem(item.id, true).then((result) => {
      if (!isMounted.current) return;
      if (result.success) {
        message.success("Item deleted");
        reload();
        // Delete persists immediately (no order Save), so also refresh the
        // surrounding order-details header — otherwise its top-right weight /
        // item quantity stay stale until the next full reload.
        if (refreshDetails) refreshDetails();
      } else {
        renderAlertMessage(result.errors);
      }
    });
  };

  const attemptCollapse = () => {
    // Collapse works on every card, including not-yet-saved "Add Item" cards
    // (user request). The mockup's "can't be closed yet" rule still applies:
    // trying to collapse a card with a live mismatch surfaces the error panel
    // instead of collapsing.
    if (!collapsed && blockers.length > 0) {
      setCloseAttempted(true);
      return;
    }
    setCloseAttempted(false);
    onToggleCollapse();
  };

  const handleHeaderClick = () => attemptCollapse();
  const handleChevronClick = (e) => {
    e.stopPropagation();
    attemptCollapse();
  };
  const handleDoneClick = () => attemptCollapse();

  // Cancel this card's edits. A never-saved card is simply dropped; a
  // persisted card reverts its draft to the saved values and collapses
  // (container's onCancelEdit). Cancel is never blocked by a mismatch — the
  // whole point is to discard the offending edits.
  const handleCancel = (e) => {
    if (e) e.stopPropagation();
    if (readOnly) return;
    if (isNew) {
      onRemoveNew();
      return;
    }
    setCloseAttempted(false);
    onCancelEdit();
  };

  const wtActual = reweighWeightAvailable(units);
  const dimActual = reweighDimAvailable(units);
  // A brand-new draft card has no server-side item yet (item is just
  // {id: tempId}) — read the meta strip straight from the draft instead of
  // the (absent) item fields, so it doesn't show a misleading "0 pcs"/"0x0x0"
  // while the field row right below it already shows the real draft values.
  // Wave A / Fix 2: Pieces must read from the draft the exact same way HUs
  // does directly below — both are live-typed fields the user watches this
  // same head-meta strip react to (Fix 1 makes HUs follow Pieces live, so a
  // headQuantity that only updated post-Save while headHandlingUnits already
  // updated live would visibly desync the two numbers while typing). A
  // transiently blank input (mid-retype) falls back to the persisted value
  // on a persisted card — same "blank draft isn't dirty" rule as HUs below.
  const draftPiecesBlank =
    draft.item_quantity === "" || draft.item_quantity === null || draft.item_quantity === undefined;
  const headQuantity = !isNew && draftPiecesBlank ? item.item_quantity : draft.item_quantity;
  const headPieceType = isNew ? draft.piece_type : item.piece_type;
  // Frontend Task 11: unlike the other head-meta fields, HUs reads straight
  // from the draft for BOTH new and persisted cards — the whole point of
  // this task is that the count in view reacts live as the user types, not
  // only after Save. `draft` always mirrors `item.handling_units` when the
  // field hasn't been touched (buildDraft's fallback), so this is a no-op
  // for an untouched persisted card and only changes anything mid-edit. A
  // transiently blank input (user cleared the box mid-retype) falls back to
  // the persisted value on a persisted card — same rule as the table's
  // displayCount (a blank draft isn't "dirty"), so the header never flickers
  // to "0 HUs" while the title below still shows the persisted count.
  const draftHUsBlank =
    draft.handling_units === "" || draft.handling_units === null || draft.handling_units === undefined;
  const headHandlingUnits = !isNew && draftHUsBlank ? item.handling_units : draft.handling_units;
  const headHandlingUnitType = isNew ? draft.handling_unit_type : item.handling_unit_type;
  // Mockup parity: the DIM strip and WT come straight off the single live item
  // object in the mockup (items-measure_36.html:1065-1069), so they move while
  // the user types. Both are pure item-form values the draft fully owns — no
  // server re-derivation stands between the draft and what Save persists — so
  // reading them live here can't show a number Save then contradicts. A
  // transiently blank box mid-retype falls back to the persisted value, the
  // same anti-flicker rule headQuantity/headHandlingUnits already use.
  const draftOrPersisted = (draftValue, persisted) =>
    !isNew && (draftValue === "" || draftValue === null || draftValue === undefined) ? persisted : draftValue;
  const headLength = draftOrPersisted(draft.item_length, item.item_length);
  const headWidth = draftOrPersisted(draft.item_width, item.item_width);
  const headHeight = draftOrPersisted(draft.item_height, item.item_height);
  const headLengthUom = draft.item_length_uom || (isNew ? "in" : item.item_length_uom);
  // WT mirrors the DIM WT logic below (and the top order panel): a reweigh
  // actual wins; else an in-progress ITEM weight-field edit wins (draft); else
  // the live per-unit booked-weight sum (so a scannable-unit weight edit moves
  // the card the same way it moves the order panel); else the draft/persisted
  // item total. numEqLb compares in lb so a uom difference doesn't read as an
  // edit.
  const numEqLb = (a, b) => Math.abs((parseFloat(a) || 0) - (parseFloat(b) || 0)) < 0.005;
  const draftWeightLbVal = toLb(draft.weight_total_exact, draft.item_weight_uom);
  const weightFieldEdited =
    isNew || !numEqLb(draftWeightLbVal, toLb(item.item_weight, item.item_weight_uom));
  const liveBookedWtLb =
    liveMeta && liveMeta.unitCount > 0 && liveMeta.bookedWeightLb != null ? liveMeta.bookedWeightLb : null;
  // WT is strictly the BOOKED weight — the re-weigh actual has its own RE-WT
  // field, so WT must never show it (user: booked and re-weigh are separate,
  // values entered in one must not appear in the other). Item weight-field
  // edit wins, else the live booked per-unit sum, else the draft/persisted
  // total.
  const displayWeightLb = weightFieldEdited
    ? draftWeightLbVal
    : liveBookedWtLb != null
    ? liveBookedWtLb
    : draftWeightLbVal;
  // DIM WT deliberately stays server-sourced. The stored value comes from
  // get_dim_values (customer_order_new_screens.rb:1805+), whose legacy math
  // multiplies by item_quantity-or-pallets depending on an org config and does
  // NOT convert cm dims to inches; mirroring those quirks client-side would
  // risk previewing a number Save contradicts — the exact failure mode the
  // rest of this wave removes.
  // DIM WT: a reweigh actual wins; otherwise, once the user has edited the
  // dims/HUs (or this is a new item), show a live value derived from the draft
  // — matching the top-panel Dim WT and the DIM (L×W×H) strip, which already
  // read the draft. A clean/untouched persisted item keeps the authoritative
  // server `item.dim_weight` (whose legacy math we don't want to contradict).
  const liveReweighWtLb = liveMeta && liveMeta.reweighWeightLb != null ? liveMeta.reweighWeightLb : null;
  const liveReweighDimLb = liveMeta && liveMeta.reweighDimLb != null ? liveMeta.reweighDimLb : null;
  // DIM WT is strictly the BOOKED dim weight — the re-weigh dim actual has its
  // own RE-DIM WT field, so this must never show it (booked / re-weigh stay
  // separate). Honors the org basis config:
  //  - 'quantity': item-level (item L×W×H / factor) × pieces, computed straight
  //    from the draft (pieces have no per-unit dims), so it tracks item-dim and
  //    Pieces edits live.
  //  - else ('handling_units'): item-dim edit wins (draft), then the live
  //    per-unit booked-dim sum (reflects per-unit dim edits), then persisted.
  // DIM WT = ((L×W×H)/factor) × (pieces or HUs per config), item-level, off the
  // draft so item-dim / Pieces / HUs edits move it live. Falls back to the
  // persisted server value only when there's no dim factor to compute with.
  // DIM WT preview mirrors the dropdown default (legacy rule): this item's own
  // factor, else the account's default factor — updates the instant the Dim
  // dropdown changes.
  const computedDimWt = itemDimWeightLb(draft, draft.dim_factor || accountDimFactor || dimFactor, dimWeightBasis);
  const displayDimWt =
    computedDimWt != null ? computedDimWt : item.dim_weight != null ? parseFloat(item.dim_weight) : null;
  // Item-level Re-Weight / Re-Dim totals (product decision: add to item
  // metrics). Prefer the live partial sums; fall back to the persisted
  // all-units totals; em-dash when there's no reweigh data at all.
  const reweighWtLb = liveReweighWtLb != null ? liveReweighWtLb : wtActual ? reweighWeightTotal(units) : null;
  const reweighDimLb = liveReweighDimLb != null ? liveReweighDimLb : dimActual ? reweighDimWeightTotal(units, item.dim_factor) : null;

  const showErrorPanel = closeAttempted && blockers.length > 0;
  const effectiveCollapsed = collapsed;

  // QA fix wave 2 / F4 (M3 mapping-wave update): "Item description should
  // reflect in place of Untitled Item" — the header title reflects the
  // live-typed DRAFT item_name first (the "Item Description" field's own
  // draft key, see itemDraftHelpers.js buildDraft — M3 maps that field to
  // item_name, not the separate item_description column), so retyping it
  // updates this title immediately instead of only after Save, with a
  // direct "Untitled Item" fallback (UnitRow's itemDisplayName keeps its own
  // item_name || item_description fallback chain, unchanged, for cross-item
  // lists like the swap picker / FlatUnitsView where item_description is
  // still a meaningful last resort).
  // The typed Item Description (mapped to item_name) drives the card's label
  // live in EVERY mode, including a not-yet-saved "Add Item" card — it only
  // falls back to a "New Item" / "Untitled Item" placeholder while still
  // blank, so filling the description immediately renames the card.
  const draftItemName = (draft.item_name || "").trim();
  const displayName = draftItemName || (isNew ? "New Item" : "Untitled Item");

  // PO point 2: keep the field grid's two visual rows intact when fields are
  // hidden. Row 1 (Item Description + Model + Serial + Storage Location) and
  // row 2 (Pieces/HUs/Weight/Dims) are rendered as SEPARATE 12-col grids so
  // hiding a row-1 field never pulls a row-2 field (weight/dims) up onto line
  // 1. Item Description absorbs whatever width the hidden row-1 fields free
  // (PO: "description takes the full width"), collapsing to its normal 6-col
  // span only when Model, Serial and Storage are all shown.
  // Legacy Dim-factor rule: the dropdown is only shown when the account has a
  // DIM factor configured; otherwise there's nothing to pick.
  const hasAccountDim = accountDimFactor !== null && accountDimFactor !== undefined && accountDimFactor !== "";
  // Dropdown default value: the item's saved factor, else the account default.
  const currentDimFactor = draft.dim_factor || accountDimFactor;
  // Options are the account's CONFIGURED factors, but always include the current
  // (saved) factor even if it was later removed from the config — so a native
  // <select> still displays the saved value as selected (matches the legacy antd
  // Select, which shows a value that isn't among its options). Only prepended
  // when it isn't already one of the configured options.
  const dimFactorOptions =
    currentDimFactor !== null && currentDimFactor !== undefined && currentDimFactor !== "" &&
    !configuredDimFactors.map((f) => String(f)).includes(String(currentDimFactor))
      ? [currentDimFactor, ...configuredDimFactors]
      : configuredDimFactors;
  // Row 1 is Item Description + Model + Serial + Storage + Dim on a 12-col grid.
  // Reserve 2 cols for the Dim dropdown only when it's shown; Item Description
  // absorbs the rest. With all fields shown this makes Description span 4, which
  // also lands Model at cols 5-6 — directly above Handling Unit Type on row 2.
  const descSpan =
    12 -
    (hasAccountDim ? 2 : 0) -
    (hiddenFields.model ? 0 : 2) -
    (hiddenFields.serial_number ? 0 : 2) -
    (hiddenFields.storage_location ? 0 : 2);
  // Class-based (not inline) span so the responsive breakpoints in the SCSS can
  // still stack it on narrow screens — an inline gridColumn would win over the
  // media-query rules and overflow the mobile grid.
  const descColClass = `col-${descSpan}`;

  // Read-only view (Figma view frame 3689:27907): the expanded card shows its
  // item fields as plain label/value pairs, NOT the disabled input boxes the
  // edit mode uses. `displayVal` renders an em-dash for anything blank so an
  // empty field reads as intentionally-empty rather than a broken cell.
  const displayVal = (v) => (v === "" || v === null || v === undefined ? "—" : v);
  const weightDisplay =
    draft.weight_input === "" || draft.weight_input === null || draft.weight_input === undefined
      ? "—"
      : `${draft.weight_input} ${draft.item_weight_uom || "lb"}`;

  // Group Items: a reduced, always-collapsed row — checkbox + name/chips +
  // the same meta strip the normal header shows, nothing else (no chevron,
  // no duplicate/delete, no editable field row/units table). Placed after
  // every calc above so it can reuse displayName/headQuantity/wtActual/etc.
  // without duplicating their derivations. The whole row is one click
  // target (matches the Figma frame, where the entire card toggles the
  // checkbox), so onToggleSelect takes no argument here — the container
  // already bound it to this item's id.
  if (groupMode) {
    return (
      <div
        className={`item-card item-card-group ${selected ? "item-card-selected" : ""}`}
        onClick={onToggleSelect}
      >
        <div className="group-row">
          <span className={`group-checkbox ${selected ? "checked" : ""}`}>
            {selected && (
              <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </span>
          <div className="group-row-body">
            <div className="group-row-top">
              <span className="item-name">{displayName}</span>
              <span className="chip">MD: {(isNew ? draft.item_model : item.item_model) || "-"}</span>
              <span className="chip">SN: {(isNew ? draft.serial_number : item.serial_number) || "-"}</span>
            </div>
            <div className="group-row-meta">
              <span>
                {headQuantity || 0} pcs ({headPieceType || "Pieces"})
              </span>
              <span>
                {headHandlingUnits || 0} HUs ({headHandlingUnitType || "Handling Units"})
              </span>
              <span>
                WT: {fmt(displayWeightLb)} lb ({fmt(lbToKg(displayWeightLb))} kg)
              </span>
              <span>
                DIM WT:{" "}
                {displayDimWt == null ? "—" : `${fmt(displayDimWt)} lb (${fmt(lbToKg(displayDimWt))} kg)`}
              </span>
              <span>
                DIM: {headLength || 0}x{headWidth || 0}x{headHeight || 0} {headLengthUom || "in"}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`item-card ${effectiveCollapsed ? "collapsed" : ""} ${isNew ? "item-card-new" : ""} ${
        dirty && !readOnly ? "item-card-dirty" : ""
      }`}
    >
      <div className="item-head" onClick={handleHeaderClick}>
        <div className="item-head-left">
          <div className="item-head-top">
            <span className="item-name">{displayName}</span>
            <span className="chip">MD: {(isNew ? draft.item_model : item.item_model) || "-"}</span>
            <span className="chip">SN: {(isNew ? draft.serial_number : item.serial_number) || "-"}</span>
          </div>
          <div className="item-head-meta">
            <span>
              {headQuantity || 0} pcs ({headPieceType || "Pieces"})
            </span>
            <span className="dot">&middot;</span>
            <span>
              {headHandlingUnits || 0} HUs ({headHandlingUnitType || "Handling Units"})
            </span>
            <span className="dot">&middot;</span>
            <span>
              WT: {fmt(displayWeightLb)} lb ({fmt(lbToKg(displayWeightLb))} kg)
            </span>
            <span className="dot">&middot;</span>
            <span>
              DIM: {headLength || 0}x{headWidth || 0}x{headHeight || 0} {headLengthUom || "in"}
            </span>
            <span className="dot">&middot;</span>
            <span>
              DIM WT:{" "}
              {displayDimWt == null ? "—" : `${fmt(displayDimWt)} lb (${fmt(lbToKg(displayDimWt))} kg)`}
            </span>
            <span className="dot">&middot;</span>
            <span>
              RE-WT:{" "}
              {reweighWtLb === null ? "—" : `${fmt(reweighWtLb)} lb (${fmt(lbToKg(reweighWtLb))} kg)`}
            </span>
            <span className="dot">&middot;</span>
            <span>
              RE-DIM WT:{" "}
              {reweighDimLb === null ? "—" : `${fmt(reweighDimLb)} lb (${fmt(lbToKg(reweighDimLb))} kg)`}
            </span>
          </div>
        </div>
        <div className="item-head-right">
          {/* Audit fix I1(a): a Save-blocking mismatch used to be invisible
              on a collapsed card — no badge/chip, and the Save button being
              disabled meant the user never naturally clicked through to the
              "This item can't be closed yet" panel to find out WHICH item
              was at fault. Reuses the same `.status-pill`/`.exception`
              styling the rollup pill above already uses. Bubbles up to
              item-head's own onClick (attemptCollapse), which already
              expands unconditionally — mismatches only block a COLLAPSE. */}
          {effectiveCollapsed && effectiveMismatches.length > 0 && (
            <span
              className="status-pill exception mismatch-badge"
              title="Piece split / weight mismatch — click to expand"
            >
              &#9888; Mismatch
            </span>
          )}
          {/* Item-level status pill removed (user request): the per-item
              rollup status is no longer shown in the card header. The ⚠ Mismatch
              badge above stays — it's an error indicator, not a status. */}
          {!readOnly && (
            <React.Fragment>
              <button type="button" className="icon-btn" title="Duplicate item" onClick={handleDuplicate}>
                <CopyIcon />
              </button>
              <Popconfirm
                title={isNew ? "Remove this item?" : "Delete this item?"}
                onConfirm={handleDelete}
                okText={isNew ? "Remove" : "Delete"}
                cancelText="Cancel"
              >
                <button type="button" className="icon-btn danger" title={isNew ? "Remove item" : "Delete item"} onClick={(e) => e.stopPropagation()}>
                  <TrashIcon />
                </button>
              </Popconfirm>
            </React.Fragment>
          )}
          {!groupMode && (
            <button type="button" className="icon-btn chevron-btn" title={effectiveCollapsed ? "Expand" : "Collapse"} onClick={handleChevronClick}>
              <ChevronIcon />
            </button>
          )}
        </div>
      </div>

      {!effectiveCollapsed && (
        <div className="item-body">
          {readOnly ? (
            // Figma view frame (3689:27907): read-only cards present the item
            // fields as static label/value pairs on the same two 12-col grids
            // the edit form uses (PO point 2 — description/model/serial/storage
            // on line 1, pieces/HUs/weight/dims on line 2, never merged), not
            // as greyed-out inputs.
            <div className="field-rows field-row-readonly">
              <div className="field-row">
                <div className={`field ${descColClass}`}>
                  <label>Item Description</label>
                  <div className="field-value">{displayVal(draft.item_name)}</div>
                </div>
                {!hiddenFields.model && (
                  <div className="field col-2">
                    <label>Model</label>
                    <div className="field-value">{displayVal(draft.item_model)}</div>
                  </div>
                )}
                {!hiddenFields.serial_number && (
                  <div className="field col-2">
                    <label>Serial Number</label>
                    <div className="field-value">{displayVal(draft.serial_number)}</div>
                  </div>
                )}
                {!hiddenFields.storage_location && (
                  <div className="field col-2">
                    <label>Storage Location</label>
                    <div className="field-value">{displayVal(draft.wh_dock)}</div>
                  </div>
                )}
                {hasAccountDim && (
                  <div className="field col-2">
                    <label>Dim</label>
                    <div className="field-value">{displayVal(draft.dim_factor || accountDimFactor)}</div>
                  </div>
                )}
              </div>
              <div className="field-row">
                {!hiddenFields.piece_type && (
                  <div className="field col-1">
                    <label>Pieces</label>
                    <div className="field-value">{displayVal(draft.item_quantity)}</div>
                  </div>
                )}
                {!hiddenFields.piece_type && (
                  <div className="field col-2">
                    <label>Piece Type</label>
                    <div className="field-value">{displayVal(draft.piece_type)}</div>
                  </div>
                )}
                {!hiddenFields.handling_unit_type && (
                  <div className="field col-1">
                    <label>HUs</label>
                    <div className="field-value">{displayVal(draft.handling_units)}</div>
                  </div>
                )}
                {!hiddenFields.handling_unit_type && (
                  <div className="field col-2">
                    <label>Handling Unit Type</label>
                    <div className="field-value">{displayVal(draft.handling_unit_type)}</div>
                  </div>
                )}
                <div className="field col-2">
                  <label>Weight</label>
                  <div className="field-value">{weightDisplay}</div>
                </div>
                <div className="field col-1">
                  <label>Length</label>
                  <div className="field-value">{displayVal(draft.item_length)}</div>
                </div>
                <div className="field col-1">
                  <label>Width</label>
                  <div className="field-value">{displayVal(draft.item_width)}</div>
                </div>
                <div className="field col-1">
                  <label>Height</label>
                  <div className="field-value">{displayVal(draft.item_height)}</div>
                </div>
                <div className="field col-1">
                  <label>UOM</label>
                  <div className="field-value">{displayVal(draft.item_length_uom)}</div>
                </div>
              </div>
            </div>
          ) : (
          <div className="field-rows">
            <div className="field-row">
              <div className={`field ${descColClass}`}>
                {/* M3 (user decision): this field reads/writes item_name (the
                    card header's name), not the separate item_description
                    column. */}
                <label>Item Description</label>
                <input
                  type="text"
                  disabled={readOnly}
                  value={draft.item_name}
                  onChange={(e) => handleFieldChange("item_name", e.target.value)}
                />
              </div>
              {!hiddenFields.model && (
                <div className="field col-2">
                  <label>Model</label>
                  <input
                    type="text"
                    disabled={readOnly}
                    value={draft.item_model}
                    onChange={(e) => handleFieldChange("item_model", e.target.value)}
                  />
                </div>
              )}
              {!hiddenFields.serial_number && (
                <div className="field col-2">
                  <label>Serial Number</label>
                  <input
                    type="text"
                    disabled={readOnly}
                    value={draft.serial_number}
                    onChange={(e) => handleFieldChange("serial_number", e.target.value)}
                  />
                </div>
              )}
              {!hiddenFields.storage_location && (
                <div className="field col-2">
                  {/* M1 (user decision, full replace): item-level Storage
                      Location maps to legacy wh_dock. */}
                  <label>Storage Location</label>
                  <input
                    type="text"
                    disabled={readOnly}
                    value={draft.wh_dock}
                    onChange={(e) => handleFieldChange("wh_dock", e.target.value)}
                  />
                </div>
              )}
              {/* Per-item Dim factor (legacy rules): options are the account's
                  configured DIM factors; default value is the item's own factor
                  or, when it has none, the account's default factor (no blank
                  placeholder). Shown only when the account has a DIM configured.
                  Sits beside Storage Location. */}
              {hasAccountDim && (
                <div className="field col-2">
                  <label>Dim</label>
                  <select
                    disabled={readOnly}
                    value={String(currentDimFactor)}
                    onChange={(e) => handleSelectChange("dim_factor", e.target.value)}
                    onKeyDown={openSelectOnEnter}
                  >
                    {dimFactorOptions.map((f) => (
                      <option key={f} value={String(f)}>{f}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="field-row">
              {!hiddenFields.piece_type && (
                <div className="field col-1">
                  <label>Pieces</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    disabled={readOnly}
                    value={draft.item_quantity}
                    onChange={(e) => handleFieldChange("item_quantity", toIntegerInput(e.target.value))}
                  />
                </div>
              )}
              {!hiddenFields.piece_type && (
                <div className="field col-2">
                  <label>Piece Type</label>
                  <select
                    disabled={readOnly}
                    value={draft.piece_type}
                    onChange={(e) => handleSelectChange("piece_type", e.target.value)}
                    onKeyDown={openSelectOnEnter}
                  >
                    {/* <option value="" disabled>
                      Select Piece Type
                    </option> */}
                    {PIECE_TYPES.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {!hiddenFields.handling_unit_type && (
                <div className="field col-1">
                  <label>HUs</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    disabled={readOnly}
                    value={draft.handling_units}
                    onChange={(e) => handleFieldChange("handling_units", toIntegerInput(e.target.value))}
                  />
                </div>
              )}
              {!hiddenFields.handling_unit_type && (
                <div className="field col-2">
                  <label>Handling Unit Type</label>
                  <select
                    disabled={readOnly}
                    value={draft.handling_unit_type}
                    onChange={(e) => handleSelectChange("handling_unit_type", e.target.value)}
                    onKeyDown={openSelectOnEnter}
                  >
                    {/* <option value="" disabled>
                      Select Handling Unit Type
                    </option> */}
                    {HANDLING_UNIT_TYPES.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="field col-2">
                <label className="label-row">
                  <span>Weight</span>
                  <select
                    className="inline-select"
                    disabled={readOnly}
                    value={draft.weight_entry_type}
                    onChange={(e) => handleSelectChange("weight_entry_type", e.target.value)}
                    onKeyDown={openSelectOnEnter}
                  >
                    <option value="total">Item Total</option>
                    <option value="per_hu">By Handling Unit</option>
                  </select>
                </label>
                <div className="unit-input">
                  <input
                    type="text"
                    inputMode="decimal"
                    disabled={readOnly}
                    value={draft.weight_input}
                    onChange={(e) => handleFieldChange("weight_input", toDecimalInput(e.target.value))}
                  />
                  <select
                    disabled={readOnly}
                    value={draft.item_weight_uom}
                    onChange={(e) => handleSelectChange("item_weight_uom", e.target.value)}
                    onKeyDown={openSelectOnEnter}
                  >
                    {["lb", "kg"].map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="field col-1">
                <label>Length</label>
                <input
                  type="text"
                  inputMode="decimal"
                  disabled={readOnly}
                  value={draft.item_length}
                  onChange={(e) => handleFieldChange("item_length", toDecimalInput(e.target.value))}
                />
              </div>
              <div className="field col-1">
                <label>Width</label>
                <input
                  type="text"
                  inputMode="decimal"
                  disabled={readOnly}
                  value={draft.item_width}
                  onChange={(e) => handleFieldChange("item_width", toDecimalInput(e.target.value))}
                />
              </div>
              <div className="field col-1">
                <label>Height</label>
                <input
                  type="text"
                  inputMode="decimal"
                  disabled={readOnly}
                  value={draft.item_height}
                  onChange={(e) => handleFieldChange("item_height", toDecimalInput(e.target.value))}
                />
              </div>
              <div className="field col-1">
                <label>UOM</label>
                <select
                  disabled={readOnly}
                  value={draft.item_length_uom}
                  onChange={(e) => handleSelectChange("item_length_uom", e.target.value)}
                  onKeyDown={openSelectOnEnter}
                >
                  {["in", "cm"].map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          )}

          {effectiveMismatches.length > 0 && (
            <div className="mismatch-chips">
              {effectiveMismatches.map((m) => (
                <span key={m.code || m.message} className="split-check mismatch">
                  {m.message}
                </span>
              ))}
            </div>
          )}

          {isNew ? (
            // Units-during-create fix: the reported bug fix. Previously this
            // branch rendered a static note and no table at all — a user had
            // no way to touch scannable units until AFTER Save created them
            // for real. Now it renders the same UnitsTable a persisted item
            // uses, seeded with N (this draft's handling_units, bounded 1..
            // 1000) fully-editable placeholder rows via
            // computeNewItemUnitsPreview — `isNewItemTable` trims the
            // per-row chrome to what makes sense before any server identity
            // exists (see UnitsTable/UnitRow's isNewItemSeed branch).
            // `deferSaves` (computed above) is true here whenever the card
            // isn't readOnly — there is no unit id to PATCH yet regardless,
            // so every edit accumulates in this card's own pendingUnitEdits
            // slice, applied post-create by handleSaveAll's existing
            // added[idx]-by-seq flush (see ItemsMeasureUnits.jsx), which
            // already resolves pending edits keyed by this card's temp id
            // via createIdMap.
            <UnitsTable
              item={item}
              units={[]}
              mode={scanMode}
              onModeChange={setScanMode}
              checkedIds={checkedIds}
              onToggleCheck={handleToggleCheck}
              onToggleAll={handleToggleAll}
              readOnly={readOnly}
              preview={preview}
              deferSaves={deferSaves}
              pendingEdits={pendingUnitEdits}
              onUnitPendingEdit={onUnitPendingEdit}
              isNewItemTable
            />
          ) : (
            <UnitsTable
              item={item}
              units={units}
              mode={scanMode}
              onModeChange={setScanMode}
              checkedIds={checkedIds}
              onToggleCheck={handleToggleCheck}
              onToggleAll={handleToggleAll}
              readOnly={readOnly}
              preview={preview}
              deferSaves={deferSaves}
              pendingEdits={pendingUnitEdits}
              onUnitPendingEdit={onUnitPendingEdit}
            />
          )}

          {/* Error panel + Done footer are shared by new (create) and saved
              cards — a create card can have a live mismatch too, and Done
              collapses the card once it's clean. */}
          {showErrorPanel && (
            <div className="split-error">
              <strong>This item can&apos;t be closed yet.</strong>
              <ul>
                {blockers.map((m) => (
                  <li key={m.code || m.message}>{mismatchPanelText(m)}</li>
                ))}
              </ul>
            </div>
          )}

          {/* "Done" collapses a card after editing — meaningless in read-only
              view (Figma view frame shows no per-card footer), so it's edit-
              only now. */}
          {!readOnly && (
            <div className="item-footer">
              {/* Per-item Cancel/Remove button removed (user request); the
                  header trash icon still handles deletion when needed. */}
              <button type="button" className="btn btn-primary" onClick={handleDoneClick}>
                Done
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Memoized: skips re-render when its props are unchanged (e.g. a keystroke in a
// sibling card). Effective as the container stabilizes the callbacks/data it
// passes; harmless before then.
export default React.memo(ItemCard);
